import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import Button from '@mui/material/Button/Button';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';

import Chip from '@mui/material/Chip';
import React, { useState } from 'react';
import { getTStateLabel, Tournament, TStateEnum } from '../../models/Tournament';
import { getEnrollmentLink } from '../../utils/Url';
import { Champions } from './champions/Champions';
import { UserRole } from '../../utils/ApiKeyData';

interface TournamentRowProps {
  tournament: Tournament;
  role?: UserRole;
  onEdit: (id: number, name: string) => void;
  onDelete: (t: Tournament) => void;
  onSchedule: (t: Tournament) => void;
  onResults: (t: Tournament) => void;
  onTrophies: (t: Tournament) => void;
  onEnrollment: (t: Tournament) => void;
}

export const TournamentRow: React.FC<TournamentRowProps> = ({
  tournament: t,
  role,
  onEdit,
  onDelete,
  onSchedule,
  onResults,
  onTrophies,
  onEnrollment,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const isLoggedIn = role !== undefined;
  const isAdmin = UserRole.Admin === role;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleTournamentClick = () => {
    if (t.state === TStateEnum.RegistrationOpen) {
      onEnrollment(t);
    } else {
      onResults(t);
    }
  };

  const handleEditClick = () => {
    handleMenuClose();
    onEdit(t.id, t.name);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    onDelete(t);
  };

  const handleScheduleClick = () => {
    handleMenuClose();
    onSchedule(t);
  };

  const handleTablesClick = () => {
    handleMenuClose();
    onResults(t);
  };

  const handleRegistrationLinkGeneration = () => {
    const link = getEnrollmentLink(t.id);
    navigator.clipboard.writeText(getEnrollmentLink(t.id)).then(() => alert('Skopiowano: ' + link));
  };

  const handleChampionsClick = () => {
    handleMenuClose();
    onTrophies(t);
  };

  return (
    <TableRow key={t.id}>
      <TableCell>
        <Button
          style={{ color: 'rgba(0, 0, 0, 0.87)', justifyContent: 'flex-start', textAlign: 'start' }}
          fullWidth
          onClick={() => handleTournamentClick()}
        >
          {t.name}
        </Button>
      </TableCell>
      <TableCell align="right">
        {isAdmin ? (
          <>
            {t.state !== undefined && (
              <Chip
                sx={{ minWidth: '11em' }}
                label={getTStateLabel(t.state)}
                variant={t.state === TStateEnum.Draft ? 'outlined' : 'filled'}
                size="small"
                color={t.state === TStateEnum.Done ? 'success' : 'info'}
              />
            )}
            <Tooltip arrow title="Więcej" placement="left">
              <IconButton onClick={handleMenuOpen}>
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={handleEditClick}>
                <ListItemIcon>
                  <Edit fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary="Edytuj" />
              </MenuItem>
              <MenuItem onClick={handleDeleteClick}>
                <ListItemIcon>
                  <Delete fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText primary="Usuń" />
              </MenuItem>
              <MenuItem onClick={handleScheduleClick}>
                <ListItemIcon>
                  <CalendarMonthIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Harmonogram" />
              </MenuItem>
              <MenuItem onClick={handleTablesClick}>
                <ListItemIcon>
                  <FormatListNumberedIcon />
                </ListItemIcon>
                <ListItemText primary="Tabele" />
              </MenuItem>
              {t.state === TStateEnum.RegistrationOpen && (
                <MenuItem onClick={handleRegistrationLinkGeneration}>
                  <ListItemIcon>
                    <HowToRegIcon />
                  </ListItemIcon>
                  <ListItemText primary="Skopiuj link do rejestracji" />
                </MenuItem>
              )}
              <MenuItem onClick={handleChampionsClick}>
                <ListItemIcon>
                  <EmojiEventsIcon />
                </ListItemIcon>
                <ListItemText primary="Edytuj wyróżnienia" />
              </MenuItem>
            </Menu>
          </>
        ) : (
          <>
            <Tooltip arrow title="Tabele" placement="left">
              <IconButton onClick={() => onResults(t)}>
                <FormatListNumberedIcon fontSize="large" />
              </IconButton>
            </Tooltip>
            <Tooltip arrow title="Harmonogram" placement="left">
              <IconButton onClick={() => onSchedule(t)}>
                <CalendarMonthIcon fontSize="large" />
              </IconButton>
            </Tooltip>
            {isLoggedIn && !isAdmin && t.state === TStateEnum.RegistrationOpen && (
              <Tooltip arrow title="Rejestracja" placement="left">
                <IconButton onClick={() => onEnrollment(t)}>
                  <HowToRegIcon fontSize="large" />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
        {t.state === TStateEnum.Done && <Champions tournament={t} />}
      </TableCell>
    </TableRow>
  );
};
